/**
 * useMentionKeys — the keyboard of the mention panel, for the thread's composer input (`composerInputProps.onKeyDown`),
 * as `useSlashCommandKeys` is for slash commands. While an "@name" is being typed and the panel lists members:
 *
 *   - ArrowUp / ArrowDown move the highlighted member;
 *   - Enter or Tab picks the highlighted one;
 *   - Esc closes the panel for this "@" (typing a new "@" opens it again);
 *
 * each with `preventDefault()`, so Enter never sends a half-typed name. Otherwise the handler does nothing. The panel
 * reports what it lists through `onOptionsChange` and registers what "pick" means through `pickRef`.
 */
import type { ConversationMemberRef } from '@confluent/shared/lib/conversation-addressing';
import { type KeyboardEvent, type MutableRefObject, useCallback, useRef, useState } from 'react';
import { mentionQuery } from './conversation-mentions.js';

export interface MentionKeys {
  highlightIndex: number;
  onOptionsChange: (options: ConversationMemberRef[]) => void;
  /** Back to the first member (the typed name changed). */
  resetHighlight: () => void;
  /** Where the "@" is whose panel was closed with Esc; null while none is. */
  dismissedAt: number | null;
  /** The panel sets this to its "pick" action. */
  pickRef: MutableRefObject<((member: ConversationMemberRef) => void) | null>;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}

export type MentionKeyAction = 'down' | 'up' | 'pick' | 'dismiss';

/**
 * What a key does to the open panel; null when the panel is closed (nothing listed, no "@" being typed, or this "@"
 * dismissed) or the key is not one of its own — then the thread's own keys (Enter to send) run.
 */
export function mentionKeyAction(
  key: { key: string; shiftKey: boolean },
  panel: { listed: number; typedAt: number | null; dismissedAt: number | null },
): MentionKeyAction | null {
  if (panel.listed === 0 || panel.typedAt === null || panel.typedAt === panel.dismissedAt) return null;
  if (key.key === 'ArrowDown') return 'down';
  if (key.key === 'ArrowUp') return 'up';
  if ((key.key === 'Enter' && !key.shiftKey) || key.key === 'Tab') return 'pick';
  if (key.key === 'Escape') return 'dismiss';
  return null;
}

export function useMentionKeys(): MentionKeys {
  const [options, setOptions] = useState<ConversationMemberRef[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const pickRef = useRef<((member: ConversationMemberRef) => void) | null>(null);

  const onOptionsChange = useCallback((next: ConversationMemberRef[]) => {
    setOptions(next);
    setHighlightIndex((current) => (next.length === 0 ? 0 : Math.min(current, next.length - 1)));
  }, []);
  const resetHighlight = useCallback(() => setHighlightIndex(0), []);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    const typed = mentionQuery(event.currentTarget.value);
    const action = mentionKeyAction(event, { listed: options.length, typedAt: typed?.start ?? null, dismissedAt });
    if (action === null) return;
    event.preventDefault();
    // Esc must not also close a side panel that listens on the window.
    event.stopPropagation();
    if (action === 'down') setHighlightIndex((current) => Math.min(options.length - 1, current + 1));
    else if (action === 'up') setHighlightIndex((current) => Math.max(0, current - 1));
    else if (action === 'pick') {
      const member = options[highlightIndex];
      if (member) pickRef.current?.(member);
    } else if (typed) setDismissedAt(typed.start);
  };

  return { highlightIndex, onOptionsChange, resetHighlight, dismissedAt, pickRef, onKeyDown };
}
